import * as React from "react";

import { useAuth } from "@/providers/auth/hooks";
import { useUserConfig } from "@/shared/domain/config/hooks";

import {
  DATE_TIME_FORMAT_CONFIG_KEY,
  DateTimeFormatPreference,
  normalizePreference,
} from "./format";
import { setActiveDateTimeFormat } from "./registry";

/**
 * Loads the user's persisted date/time format preference from backend config
 * and publishes it into the module-level registry so both React components
 * (via `useDateTimeFormat`) and pure formatters (via the `formatters` module)
 * pick it up.
 *
 * Renders children immediately; until the config query resolves the registry
 * holds the locale default, so first paint shows sensible dates and updates
 * in place once the preference arrives. Tolerates the unauthenticated case
 * (no data / query error) by leaving the default in place.
 *
 * The registry is process-wide and outlives any single session — sign-out and
 * sign-in happen entirely in-SPA — so the published preference is gated on
 * `currentUser`: signing out drops back to the locale default rather than
 * leaving the departing user's format applied to whoever signs in next. The
 * `userConfig` query is partitioned by user id for the same reason, so `data`
 * is never another user's cached value.
 *
 * Must be mounted inside the query client, auth, and domain-services providers.
 */
export function DateTimeFormatProvider({
  children,
}: React.PropsWithChildren): React.ReactElement {
  const { currentUser } = useAuth();
  const { data } = useUserConfig<DateTimeFormatPreference>(
    DATE_TIME_FORMAT_CONFIG_KEY,
  );
  const preference = currentUser === null ? null : (data ?? null);

  React.useEffect(() => {
    setActiveDateTimeFormat(normalizePreference(preference));
  }, [preference]);

  return <>{children}</>;
}
